import { CALENDAR_PACKAGE_OPTIONS, findPartyPackage } from "../constants/packageDefaults";
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import "../css/PrivateEventLeads.css";

function normalizePhone(value) {
  return String(value || "").replace(/\D/g, "");
}

function toMillis(ts) {
  if (!ts) return 0;
  if (typeof ts === "number") return ts;
  if (typeof ts?.toMillis === "function") return ts.toMillis();
  const ms = new Date(ts).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

function todayYMD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isPastYMD(ymd) {
  if (!ymd) return false;
  const a = new Date(`${ymd}T00:00:00`).getTime();
  const b = new Date(`${todayYMD()}T00:00:00`).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return a < b;
}

/**
 * Lead = private event "note" / follow-up card.
 * Stored in Firestore collection: privateEventLeads
 */
export default function PrivateEventLeads({ onBookLead }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showVoided, setShowVoided] = useState(false);
  const [search, setSearch] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [busyId, setBusyId] = useState(null);

  const packageOptions = CALENDAR_PACKAGE_OPTIONS;

  const [newLead, setNewLead] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    eventDate: "",
    startTime: "10:00",
    endTime: "12:00",
    packageType: "",
    details: "",
  });

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError("");
      const qRef = query(
        collection(db, "privateEventLeads"),
        orderBy("createdAt", "desc"),
        limit(200)
      );
      const snap = await getDocs(qRef);
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Auto-void notes whose intended event date has already passed.
      // (Skip notes already voided/booked.)
      const toVoid = snap.docs.filter((d) => {
        const data = d.data() || {};
        const status = data.status || "open";
        if (status === "voided" || status === "booked") return false;
        return isPastYMD(data.eventDate);
      });
      if (toVoid.length) {
        const batch = writeBatch(db);
        // Firestore batch limit is 500 writes; keep it conservative.
        toVoid.slice(0, 400).forEach((d) => {
          batch.update(d.ref, {
            status: "voided",
            voidedAt: serverTimestamp(),
            voidReason: "datePassed",
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();

        const voidedIds = new Set(toVoid.slice(0, 400).map((d) => d.id));
        rows.forEach((r) => {
          if (voidedIds.has(r.id)) r.status = "voided";
        });
      }
      setLeads(rows);
    } catch (e) {
      console.error("Error fetching private event leads:", e);
      setError("Could not load leads. See console for details.");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const filtered = useMemo(() => {
    const q = String(search || "").trim().toLowerCase();
    return (leads || [])
      .filter((l) => (showVoided ? true : (l.status || "open") !== "voided"))
      .filter((l) => {
        if (!q) return true;
        const phone = normalizePhone(l.phoneNumber || l.PhoneNum || "");
        const name = `${l.firstName || ""} ${l.lastName || ""}`.trim();
        return (
          phone.includes(normalizePhone(q)) ||
          name.toLowerCase().includes(q) ||
          String(l.details || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
  }, [leads, search, showVoided]);

  const startEdit = (lead) => {
    setEditingId(lead.id);
    setDraft({
      firstName: lead.firstName || "",
      lastName: lead.lastName || "",
      phoneNumber: lead.phoneNumber || "",
      email: lead.email || "",
      eventDate: lead.eventDate || "",
      startTime: lead.startTime || "10:00",
      endTime: lead.endTime || "12:00",
      packageType: lead.packageType || "",
      details: lead.details || "",
      status: lead.status || "open",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async (lead) => {
    if (!lead?.id) return;
    setBusyId(lead.id);
    try {
      const payload = {
        firstName: draft.firstName || "",
        lastName: draft.lastName || "",
        phoneNumber: draft.phoneNumber || "",
        email: draft.email || "",
        eventDate: draft.eventDate || "",
        startTime: draft.startTime || "10:00",
        endTime: draft.endTime || "12:00",
        packageType: draft.packageType || "",
        details: draft.details || "",
        status: draft.status || "open",
        updatedAt: serverTimestamp(),
      };
      await updateDoc(doc(db, "privateEventLeads", lead.id), payload);
      setLeads((prev) =>
        (prev || []).map((l) => (l.id === lead.id ? { ...l, ...payload } : l))
      );
      cancelEdit();
    } catch (e) {
      console.error("Error updating lead:", e);
      alert("Failed to update lead. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  const voidLead = async (lead) => {
    if (!lead?.id) return;
    const ok = window.confirm("Void this lead? (You can still show voided.)");
    if (!ok) return;
    setBusyId(lead.id);
    try {
      await updateDoc(doc(db, "privateEventLeads", lead.id), {
        status: "voided",
        voidedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setLeads((prev) =>
        (prev || []).map((l) =>
          l.id === lead.id ? { ...l, status: "voided" } : l
        )
      );
      if (editingId === lead.id) cancelEdit();
    } catch (e) {
      console.error("Error voiding lead:", e);
      alert("Failed to void lead. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  const deleteLead = async (lead) => {
    if (!lead?.id) return;
    const ok = window.confirm(
      "Delete this lead permanently? This cannot be undone."
    );
    if (!ok) return;
    setBusyId(lead.id);
    try {
      await deleteDoc(doc(db, "privateEventLeads", lead.id));
      setLeads((prev) => (prev || []).filter((l) => l.id !== lead.id));
      if (editingId === lead.id) cancelEdit();
    } catch (e) {
      console.error("Error deleting lead:", e);
      alert("Failed to delete lead. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  const addLead = async (e) => {
    e.preventDefault();
    const phoneId = normalizePhone(newLead.phoneNumber);
    if (!newLead.firstName.trim() && !newLead.lastName.trim()) {
      alert("Please enter a name.");
      return;
    }
    if (phoneId.length < 10) {
      alert("Please enter a valid phone number (10 digits).");
      return;
    }
    if (!newLead.eventDate) {
      alert("Please select an event date.");
      return;
    }
    if (!newLead.startTime || !newLead.endTime) {
      alert("Please enter a start and end time.");
      return;
    }
    if (!newLead.packageType) {
      alert("Please select a package type.");
      return;
    }
    setBusyId("__new__");
    try {
      const payload = {
        firstName: newLead.firstName.trim(),
        lastName: newLead.lastName.trim(),
        phoneNumber: newLead.phoneNumber.trim(),
        email: newLead.email.trim(),
        eventDate: newLead.eventDate,
        startTime: newLead.startTime,
        endTime: newLead.endTime,
        packageType: newLead.packageType,
        details: newLead.details.trim(),
        status: "open",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, "privateEventLeads"), payload);
      setLeads((prev) => [{ id: ref.id, ...payload }, ...(prev || [])]);
      setNewLead({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        eventDate: "",
        startTime: "10:00",
        endTime: "12:00",
        packageType: "",
        details: "",
      });
    } catch (e2) {
      console.error("Error adding lead:", e2);
      alert("Failed to add lead. See console for details.");
    } finally {
      setBusyId(null);
    }
  };

  const bookLead = (lead) => {
    if (!lead?.eventDate) {
      alert("Add an event date to this note (Edit) before booking.");
      return;
    }
    if (!lead?.startTime || !lead?.endTime) {
      alert("Add a start/end time to this note (Edit) before booking.");
      return;
    }
    if (!lead?.packageType) {
      alert("Add a package type to this note (Edit) before booking.");
      return;
    }
    if (typeof onBookLead === "function") onBookLead(lead);
  };

  return (
    <div className="private-event-leads">
      <h2>Private Event Notes</h2>

      <form className="lead-form card" onSubmit={addLead}>
        <div className="grid">
          <div className="field">
            <label>First Name</label>
            <input
              value={newLead.firstName}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, firstName: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label>Last Name</label>
            <input
              value={newLead.lastName}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, lastName: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label>Phone</label>
            <input
              value={newLead.phoneNumber}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, phoneNumber: e.target.value }))
              }
              placeholder="e.g. 8185551234"
            />
          </div>
          <div className="field">
            <label>Email (optional)</label>
            <input
              value={newLead.email}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, email: e.target.value }))
              }
            />
          </div>
          <div className="field">
            <label>Event Date</label>
            <input
              type="date"
              value={newLead.eventDate}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, eventDate: e.target.value }))
              }
              required
            />
          </div>
          <div className="field">
            <label>Start Time</label>
            <input
              type="time"
              value={newLead.startTime}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, startTime: e.target.value }))
              }
              required
            />
          </div>
          <div className="field">
            <label>End Time</label>
            <input
              type="time"
              value={newLead.endTime}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, endTime: e.target.value }))
              }
              required
            />
          </div>
          <div className="field">
            <label>Package Type</label>
            <select
              value={newLead.packageType}
              onChange={(e) =>
                setNewLead((p) => ({ ...p, packageType: e.target.value }))
              }
              required
            >
              <option value="">Select package</option>
              {packageOptions.map((p) => (
                <option key={p} value={p}>
                  {findPartyPackage(p)?.name || p}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label>Conversation / Details</label>
          <textarea
            rows={4}
            value={newLead.details}
            onChange={(e) =>
              setNewLead((p) => ({ ...p, details: e.target.value }))
            }
            placeholder="What did they ask? Preferred date/time? Budget? Package? Follow-up notes..."
          />
        </div>

        <div className="actions">
          <button className="btn-primary" type="submit" disabled={busyId === "__new__"}>
            {busyId === "__new__" ? "Adding..." : "Add Note"}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={fetchLeads}
            disabled={loading}
            title="Refresh list"
          >
            Refresh
          </button>
        </div>
      </form>

      <div className="lead-toolbar">
        <input
          className="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name / phone / details..."
        />
        <label className="toggle">
          <input
            type="checkbox"
            checked={showVoided}
            onChange={(e) => setShowVoided(e.target.checked)}
          />
          Show voided
        </label>
      </div>

      {error && <div className="error">{error}</div>}
      {loading && <div className="muted">Loading...</div>}

      {filtered.length === 0 && !loading ? (
        <div className="muted">No notes yet.</div>
      ) : (
        <div className="lead-list">
          {filtered.map((l) => {
            const isEditing = editingId === l.id;
            const status = l.status || "open";
            return (
              <div key={l.id} className={`lead card status-${status}`}>
                <div className="lead-top">
                  <div className="lead-title">
                    {isEditing ? (
                      <>
                        <input
                          className="inline"
                          value={draft.firstName}
                          onChange={(e) =>
                            setDraft((p) => ({ ...p, firstName: e.target.value }))
                          }
                          placeholder="First"
                        />
                        <input
                          className="inline"
                          value={draft.lastName}
                          onChange={(e) =>
                            setDraft((p) => ({ ...p, lastName: e.target.value }))
                          }
                          placeholder="Last"
                        />
                      </>
                    ) : (
                      <strong>
                        {`${l.firstName || ""} ${l.lastName || ""}`.trim() ||
                          "Unnamed"}
                      </strong>
                    )}
                    <span className={`badge ${status}`}>{status}</span>
                  </div>
                  <div className="lead-buttons">
                    {isEditing ? (
                      <>
                        <button
                          className="btn-small btn-primary"
                          disabled={busyId === l.id}
                          onClick={() => saveEdit(l)}
                          type="button"
                        >
                          {busyId === l.id ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="btn-small btn-ghost"
                          onClick={cancelEdit}
                          type="button"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="btn-small"
                          onClick={() => startEdit(l)}
                          type="button"
                        >
                          Edit
                        </button>
                        <button
                          className="btn-small btn-primary"
                          onClick={() => bookLead(l)}
                          type="button"
                          title="Pre-fill Private Events booking form"
                        >
                          Book
                        </button>
                        <button
                          className="btn-small btn-warn"
                          onClick={() => voidLead(l)}
                          type="button"
                          disabled={busyId === l.id}
                        >
                          Void
                        </button>
                        <button
                          className="btn-small btn-danger"
                          onClick={() => deleteLead(l)}
                          type="button"
                          disabled={busyId === l.id}
                        >
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="lead-meta">
                  <div>
                    <span className="label">Phone:</span>{" "}
                    {isEditing ? (
                      <input
                        className="inline wide"
                        value={draft.phoneNumber}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, phoneNumber: e.target.value }))
                        }
                      />
                    ) : (
                      l.phoneNumber || "—"
                    )}
                  </div>
                  <div>
                    <span className="label">Email:</span>{" "}
                    {isEditing ? (
                      <input
                        className="inline wide"
                        value={draft.email}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, email: e.target.value }))
                        }
                      />
                    ) : (
                      l.email || "—"
                    )}
                  </div>
                  <div>
                    <span className="label">Date:</span>{" "}
                    {isEditing ? (
                      <input
                        type="date"
                        className="inline"
                        value={draft.eventDate}
                        onChange={(e) =>
                          setDraft((p) => ({ ...p, eventDate: e.target.value }))
                        }
                      />
                    ) : (
                      l.eventDate || "—"
                    )}
                  </div>
                  <div>
                    <span className="label">Time:</span>{" "}
                    {isEditing ? (
                      <div className="inline-time">
                        <input
                          type="time"
                          className="inline"
                          value={draft.startTime}
                          onChange={(e) =>
                            setDraft((p) => ({
                              ...p,
                              startTime: e.target.value,
                            }))
                          }
                        />
                        <span className="muted">to</span>
                        <input
                          type="time"
                          className="inline"
                          value={draft.endTime}
                          onChange={(e) =>
                            setDraft((p) => ({ ...p, endTime: e.target.value }))
                          }
                        />
                      </div>
                    ) : l.startTime && l.endTime ? (
                      `${l.startTime} – ${l.endTime}`
                    ) : (
                      "—"
                    )}
                  </div>
                  <div className="meta-wide">
                    <span className="label">Package:</span>{" "}
                    {isEditing ? (
                      <select
                        className="inline wide"
                        value={draft.packageType}
                        onChange={(e) =>
                          setDraft((p) => ({
                            ...p,
                            packageType: e.target.value,
                          }))
                        }
                      >
                        <option value="">Select package</option>
                        {packageOptions.map((p) => (
                          <option key={p} value={p}>
                            {findPartyPackage(p)?.name || p}
                          </option>
                        ))}
                      </select>
                    ) : (
                      l.packageType || "—"
                    )}
                  </div>
                </div>

                <div className="lead-details">
                  {isEditing ? (
                    <textarea
                      rows={4}
                      value={draft.details}
                      onChange={(e) =>
                        setDraft((p) => ({ ...p, details: e.target.value }))
                      }
                    />
                  ) : (
                    <pre>{l.details || "—"}</pre>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
